import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Menu, X } from "lucide-react";
import useIsDarkTheme from "../../../core/hooks/useIsDarkTheme";
import Wordmark from "../Wordmark/Wordmark";
import {
  PUBLIC_HEADER_CREATE_ACCOUNT,
  PUBLIC_HEADER_LINKS,
  PUBLIC_HEADER_MENU_ID,
  isPublicHeaderLinkCurrent,
  publicHeaderClass,
} from "./publicHeaderLinks";
import "./PublicHeader.css";

/** Same cut as PublicHeader.css `@media (min-width: 768px)`. */
const WIDE_QUERY = "(min-width: 768px)";
const HEADER_FOCUSABLE = "a[href], button:not([disabled])";

function readNarrow(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return !window.matchMedia(WIDE_QUERY).matches;
}

function visibleHeaderFocusables(header: HTMLElement): HTMLElement[] {
  return Array.from(
    header.querySelectorAll<HTMLElement>(HEADER_FOCUSABLE),
  ).filter((element) => element.getClientRects().length > 0);
}

/**
 * Moves focus to the next visible header link or button. The dialog element
 * is the opening focus and is not in that set, so Tab from it continues to
 * the next control in document order, and Shift+Tab to the previous one.
 */
function focusInCycle(header: HTMLElement, shift: boolean) {
  const items = visibleHeaderFocusables(header);
  if (items.length === 0) return;

  const active = document.activeElement;
  const index = active instanceof HTMLElement ? items.indexOf(active) : -1;
  if (index >= 0) {
    const nextIndex = shift
      ? (index - 1 + items.length) % items.length
      : (index + 1) % items.length;
    items[nextIndex].focus();
    return;
  }

  const from = active instanceof Node ? active : header;
  const bit = shift
    ? Node.DOCUMENT_POSITION_PRECEDING
    : Node.DOCUMENT_POSITION_FOLLOWING;
  const ordered = shift ? [...items].reverse() : items;
  const next = ordered.find(
    (element) => (from.compareDocumentPosition(element) & bit) !== 0,
  );
  (next ?? items[shift ? items.length - 1 : 0]).focus();
}

export default function PublicHeader() {
  const { pathname, hash } = useLocation();
  const isDark = useIsDarkTheme();
  const [open, setOpen] = useState(false);
  const [narrow, setNarrow] = useState(() => readNarrow());
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const media = window.matchMedia(WIDE_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      setNarrow(!event.matches);
      if (event.matches) setOpen(false);
    };
    setNarrow(!media.matches);
    if (media.matches) setOpen(false);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!open || !narrow) return;
    dialogRef.current?.focus();
  }, [open, narrow]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (
          event.repeat ||
          event.ctrlKey ||
          event.metaKey ||
          event.altKey ||
          event.shiftKey
        ) {
          return;
        }
        event.preventDefault();
        setOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      if (event.key !== "Tab" || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      const header = headerRef.current;
      if (!header) return;
      event.preventDefault();
      focusInCycle(header, event.shiftKey);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const onMenuClick = () => {
    if (!readNarrow()) return;
    setNarrow(true);
    setOpen((value) => !value);
  };

  const closeMenu = () => setOpen(false);

  return (
    <header
      ref={headerRef}
      className={publicHeaderClass.root}
      data-open={open ? "true" : "false"}
    >
      <div className={publicHeaderClass.bar}>
        <Wordmark
          width={190}
          to="/"
          label="Acertei Questões"
          variant={open || isDark ? "dark" : "light"}
        />
        <div className={publicHeaderClass.spacer} />
        <nav className={publicHeaderClass.nav} aria-label="Navegação">
          {PUBLIC_HEADER_LINKS.map((link) => {
            const current = isPublicHeaderLinkCurrent(link.to, pathname, hash);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={publicHeaderClass.link}
                aria-current={current ? "page" : undefined}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>
        <button
          ref={menuButtonRef}
          type="button"
          className={publicHeaderClass.menu}
          aria-label="Abrir menu"
          aria-expanded={open}
          aria-controls={PUBLIC_HEADER_MENU_ID}
          style={{ minWidth: 44, minHeight: 44 }}
          onClick={onMenuClick}
        >
          {open ? (
            <X size={24} aria-hidden="true" />
          ) : (
            <Menu size={24} aria-hidden="true" />
          )}
        </button>
      </div>
      {open && narrow ? (
        <div
          id={PUBLIC_HEADER_MENU_ID}
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className={publicHeaderClass.dialog}
          tabIndex={-1}
          ref={dialogRef}
        >
          <div className={publicHeaderClass.decor} aria-hidden="true">
            <span className={publicHeaderClass.decorTeal} />
            <span className={publicHeaderClass.decorEmerald} />
            <span className={publicHeaderClass.decorAmber} />
            <span className={publicHeaderClass.decorSky} />
          </div>
          <nav className={publicHeaderClass.dialogNav}>
            {PUBLIC_HEADER_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={publicHeaderClass.item}
                onClick={closeMenu}
              >
                <span>{link.label}</span>
                <ChevronRight size={22} aria-hidden="true" />
              </Link>
            ))}
            <Link
              to={PUBLIC_HEADER_CREATE_ACCOUNT.to}
              className={publicHeaderClass.cta}
              onClick={closeMenu}
            >
              {PUBLIC_HEADER_CREATE_ACCOUNT.label}
            </Link>
          </nav>
        </div>
      ) : null}
    </header>
  );
}
